const WIKIPEDIA_SUMMARY_URL = 'https://en.wikipedia.org/api/rest_v1/page/summary/';

const TOPIC_RULES = [
  {
    category: 'celestial',
    label: 'Auroras',
    query: 'Aurora',
    keywords: ['aurora', 'northern lights', 'southern lights'],
  },
  {
    category: 'celestial',
    label: 'Meteor showers',
    query: 'Meteor shower',
    keywords: ['meteor', 'perseid', 'leonid', 'geminid', 'lyrid', 'quadrantid'],
  },
  {
    category: 'celestial',
    label: 'Eclipses',
    query: 'Eclipse',
    keywords: ['eclipse'],
  },
  {
    category: 'celestial',
    label: 'Moon phases and lunar viewing',
    query: 'Moon',
    keywords: ['full moon', 'new moon', 'supermoon', 'moonrise', 'moonset', 'moon'],
  },
  {
    category: 'celestial',
    label: 'Planet alignments and conjunctions',
    query: 'Conjunction (astronomy)',
    keywords: ['alignment', 'conjunction', 'planet'],
  },
  {
    category: 'celestial',
    label: 'Space weather',
    query: 'Space weather',
    keywords: ['solar activity', 'solar storm', 'geomagnetic', 'sunspot', 'kp', 'space weather'],
  },
  {
    category: 'earth_systems',
    label: 'Upwelling',
    query: 'Upwelling',
    keywords: ['upwelling'],
  },
  {
    category: 'earth_systems',
    label: 'Atmospheric rivers',
    query: 'Atmospheric river',
    keywords: ['atmospheric river'],
  },
  {
    category: 'earth_systems',
    label: 'Tides and coastal flooding',
    query: 'Tide',
    keywords: ['king tide', 'high tide', 'extreme tide', 'coastal flooding', 'tide'],
  },
  {
    category: 'earth_systems',
    label: 'Marine heatwaves',
    query: 'Marine heatwave',
    keywords: ['marine heatwave'],
  },
  {
    category: 'earth_systems',
    label: 'Algal blooms',
    query: 'Algal bloom',
    keywords: ['algal bloom', 'red tide', 'harmful algal'],
  },
  {
    category: 'earth_systems',
    label: 'Phytoplankton blooms',
    query: 'Phytoplankton',
    keywords: ['phytoplankton'],
  },
  {
    category: 'earth_systems',
    label: 'Ocean swell',
    query: 'Swell (ocean)',
    keywords: ['storm swell', 'swell'],
  },
];

const enrichmentCache = new Map();

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function makeCacheKey(item) {
  return `${normalizeText(item?.category)}|${normalizeText(item?.title || item?.name)}`;
}

function categorySupportsLearning(category) {
  return ['celestial', 'earth_systems'].includes(normalizeText(category));
}

function resolveTopicRule(item) {
  const category = normalizeText(item?.category || item?.category_label);
  if (!categorySupportsLearning(category)) return null;

  const haystack = normalizeText(
    [item?.title, item?.name, item?.summary, item?.description, item?.event_type].filter(Boolean).join(' ')
  );

  const matchingRule = TOPIC_RULES.find(
    (rule) => rule.category === category && rule.keywords.some((keyword) => haystack.includes(keyword))
  );

  if (matchingRule) return matchingRule;

  return {
    category,
    label: item?.category_label || item?.category || 'Phenomenon',
    query: item?.title || item?.name || '',
    keywords: [],
  };
}

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.json();
}

export function supportsEventEnrichment(item) {
  return Boolean(resolveTopicRule(item)?.query);
}

export async function fetchEventEnrichment(item) {
  const cacheKey = makeCacheKey(item);
  if (enrichmentCache.has(cacheKey)) {
    return enrichmentCache.get(cacheKey);
  }

  const topicRule = resolveTopicRule(item);
  if (!topicRule?.query) {
    return null;
  }

  try {
    const summary = await fetchJson(`${WIKIPEDIA_SUMMARY_URL}${encodeURIComponent(topicRule.query)}`);
    const payload = {
      label: topicRule.label,
      summary:
        summary?.extract ||
        `Open reference material is available for ${topicRule.label.toLowerCase()}, but the summary could not be loaded right now.`,
      learnUrl: summary?.content_urls?.desktop?.page || '',
      image:
        summary?.originalimage?.source || summary?.thumbnail?.source
          ? {
              src: summary?.originalimage?.source || summary?.thumbnail?.source || '',
              sourceLabel: 'Wikipedia / Wikimedia',
              pageUrl: summary?.content_urls?.desktop?.page || '',
            }
          : null,
    };
    enrichmentCache.set(cacheKey, payload);
    return payload;
  } catch (_error) {
    const fallback = {
      label: topicRule.label,
      summary: `Open-source background material exists for ${topicRule.label.toLowerCase()}, but the live explainer could not be loaded right now.`,
      learnUrl: '',
      image: null,
    };
    enrichmentCache.set(cacheKey, fallback);
    return fallback;
  }
}

export function clearEventEnrichmentCache() {
  enrichmentCache.clear();
}
